import React from 'react';
import styled from 'styled-components';
import { SlideComponentType } from '../data/slides';
import { appTheme } from '../styles/theme';

const ICON_TINTS: Record<SlideComponentType, string> = {
  rectangle: '#dceee8',
  ellipse: '#d4ebf7',
  line: '#e8ecea',
  image: '#fae8dc',
  text: '#fff0e3',
  table: '#e4f0ec',
  chart: '#dce8f4',
};

const STROKE = appTheme.greenDark;

const Frame = styled.div<{ $tint: string }>`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: radial-gradient(circle at 28% 22%, #ffffff 0%, ${({ $tint }) => $tint} 68%);
  box-shadow:
    0 4px 10px rgba(10, 46, 40, 0.07),
    0 1px 2px rgba(10, 46, 40, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Svg = styled.svg`
  width: 18px;
  height: 18px;
  display: block;
`;

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: STROKE,
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const RectangleIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="5" y="7" width="14" height="10" rx="1.5" />
  </Svg>
);

const EllipseIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <circle cx="12" cy="12" r="6.5" />
  </Svg>
);

const LineIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <path d="M5 12h14" />
    <circle cx="5" cy="12" r="1.25" fill={STROKE} stroke="none" />
    <circle cx="19" cy="12" r="1.25" fill={STROKE} stroke="none" />
  </Svg>
);

const ImageIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="4" y="5" width="16" height="14" rx="1.5" />
    <circle cx="9" cy="10" r="1.5" />
    <path d="M4 16.5 9.5 12 13 14.5 16.5 11.5 20 15" />
  </Svg>
);

const TextIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <path d="M7 6.5v11M7 6.5h6M7 12h4" />
    <path d="M15 8.5h2.5M15 12h2M15 15.5h2.5" />
  </Svg>
);

const TableIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="4" y="5" width="16" height="14" rx="1.5" />
    <path d="M4 10h16M4 14.5h16M10.5 5v14M15.5 5v14" />
  </Svg>
);

const ChartIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <path d="M5 19V5" />
    <path d="M5 19h14" />
    <rect x="8" y="13" width="2.5" height="6" rx="0.5" fill={STROKE} stroke="none" />
    <rect x="12.25" y="9" width="2.5" height="10" rx="0.5" fill={STROKE} stroke="none" />
    <rect x="16.5" y="11" width="2.5" height="8" rx="0.5" fill={STROKE} stroke="none" />
  </Svg>
);

const ICONS: Record<SlideComponentType, React.FC> = {
  rectangle: RectangleIcon,
  ellipse: EllipseIcon,
  line: LineIcon,
  image: ImageIcon,
  text: TextIcon,
  table: TableIcon,
  chart: ChartIcon,
};

interface ComponentIconProps {
  type: SlideComponentType;
  className?: string;
}

const ComponentIcon: React.FC<ComponentIconProps> = ({ type, className }) => {
  const Icon = ICONS[type];

  return (
    <Frame $tint={ICON_TINTS[type]} className={className} aria-hidden>
      <Icon />
    </Frame>
  );
};

export default ComponentIcon;
